import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Globe, Users, Briefcase, Lock, 
  CheckCircle, DollarSign, MessageSquare, 
  FileText, Zap, ChevronRight, Layout,
  Twitter, Facebook, Linkedin, Instagram,
} from 'lucide-react';
import { motion } from 'framer-motion';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-white font-[Poppins] text-gray-800">
      
      {/* --- HERO SECTION: The Appointment Economy --- */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#2E86DE] to-[#10AC84] text-white py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6"
          >
            The Appointment Economy, <span className="text-[#F39C12]">Simplified.</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto font-light"
          >
            Connecting Global Talent with High-Value Opportunities.
            <br/>
            <span className="text-sm md:text-lg opacity-80 mt-2 block">
              A transparent, secure, and anonymous marketplace where high-quality appointment setting meets verified business demand.
            </span>
          </motion.p>
        </div>
        
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white blur-3xl"></div>
            <div className="absolute top-1/2 right-0 w-64 h-64 rounded-full bg-[#F39C12] blur-3xl"></div>
        </div>
      </div>

      {/* --- BLOCK 1: THE ECOSYSTEM (Trust Layer) --- */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#2E86DE] font-bold tracking-wider uppercase text-sm">The Architecture</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Revenue Setters as the Trust Layer</h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              We eliminate friction and risk. By maintaining strict anonymity, companies and setters never interact directly—only the platform has full visibility, securing transaction integrity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center"
            >
              <div className="p-4 bg-blue-50 rounded-full text-[#2E86DE] mb-6">
                <Lock size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3">Total Anonymity</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Supply and demand are decoupled. Identities are protected until the transaction is secured by the platform.
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center"
            >
              <div className="p-4 bg-green-50 rounded-full text-[#10AC84] mb-6">
                <Globe size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3">Global Payment Infrastructure</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We collect payments securely from companies and facilitate seamless global disbursements to setters worldwide.
              </p>
            </motion.div>

            {/* Feature 3 */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center"
            >
              <div className="p-4 bg-yellow-50 rounded-full text-[#F39C12] mb-6">
                <Shield size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3">Accountability Rating</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                A dual-sided rating system scores Setters on lead quality and Companies on professionalism and payment speed.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* --- BLOCK 2: FOR SETTERS (The Supply) --- */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold uppercase">The Supply Side</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-4 mb-6">
                Turn Networking into Scalable Income
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Register by your industry expertise (Janitorial, MCA, Real Estate)  and monetize your connections through our standardized verification process.
              </p>
              
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle className="text-[#10AC84]" size={20} />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900">Detailed Lead Submission</h4>
                    <p className="text-gray-500 text-sm mt-1">
                      Fill out standardized fields: Business Name, Appointment Date, Decision Maker details, Facility Size, Scope, and Urgency .
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <FileText className="text-[#10AC84]" size={20} />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900">Mandatory Proof</h4>
                    <p className="text-gray-500 text-sm mt-1">
                      Quality is our currency. Upload email screenshots or call recordings to prove the appointment is genuine.
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <DollarSign className="text-[#10AC84]" size={20} />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900">Track & Earn</h4>
                    <p className="text-gray-500 text-sm mt-1">
                      Monitor approval status in real-time and view your earnings dashboard for payout status.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <Link to="/register" className="text-[#2E86DE] font-bold flex items-center hover:underline">
                  Start setting appointments <ChevronRight size={16} />
                </Link>
              </div>
            </div>
            
            <div className="lg:w-1/2 bg-gray-50 rounded-2xl p-8 border border-gray-200 shadow-xl rotate-1 hover:rotate-0 transition duration-500">
               {/* Mock UI Representation */}
               <div className="bg-white rounded-xl shadow-sm p-6 mb-4 border border-gray-100">
                  <div className="flex justify-between items-center mb-4">
                      <div className="h-2 w-20 bg-gray-200 rounded"></div>
                      <div className="h-6 w-16 bg-green-100 rounded-full text-xs text-green-700 flex items-center justify-center font-bold">APPROVED</div>
                  </div>
                  <div className="space-y-3">
                      <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
                      <div className="h-4 w-1/2 bg-gray-100 rounded"></div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center">
                      <div className="text-xs text-gray-400">Payout</div>
                      <div className="text-lg font-bold text-green-600">$150.00</div>
                  </div>
               </div>
               <div className="bg-white rounded-xl shadow-sm p-6 opacity-60 border border-gray-100">
                  <div className="flex justify-between items-center mb-4">
                      <div className="h-2 w-20 bg-gray-200 rounded"></div>
                      <div className="h-6 w-16 bg-yellow-100 rounded-full"></div>
                  </div>
                  <div className="space-y-3">
                      <div className="h-4 w-full bg-gray-100 rounded"></div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- BLOCK 3: FOR COMPANIES (The Demand) --- */}
      <div className="py-20 bg-[#222F3E] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
            
            <div className="lg:w-1/2">
              <span className="bg-[#F39C12] text-white px-3 py-1 rounded-full text-xs font-bold uppercase">The Demand Side</span>
              <h2 className="text-3xl md:text-4xl font-extrabold mt-4 mb-6">
                Access Verified Revenue Pipelines
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Eliminate the overhead of internal SDR teams. Register by sector (Commercial Cleaning, Finance, etc.) and receive instant alerts via Email or WhatsApp.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/5 p-6 rounded-xl border border-white/10 hover:bg-white/10 transition">
                    <Layout className="text-[#F39C12] mb-4" size={28} />
                    <h4 className="text-lg font-bold mb-2">Hidden Details</h4>
                    <p className="text-sm text-gray-400">
                        Browse partial lead details safely. Critical contact info remains hidden until purchase to protect asset value.
                    </p>
                </div>
                <div className="bg-white/5 p-6 rounded-xl border border-white/10 hover:bg-white/10 transition">
                    <MessageSquare className="text-[#F39C12] mb-4" size={28} />
                    <h4 className="text-lg font-bold mb-2">Dual-Action Interface</h4>
                    <p className="text-sm text-gray-400">
                        Use the dedicated chatbox to query admins for clarification, or "Claim" to purchase instantly.
                    </p>
                </div>
                <div className="bg-white/5 p-6 rounded-xl border border-white/10 hover:bg-white/10 transition md:col-span-2">
                    <Shield className="text-[#F39C12] mb-4" size={28} />
                    <h4 className="text-lg font-bold mb-2">Risk-Free Assurance</h4>
                    <p className="text-sm text-gray-400">
                        We emphasize trust. [cite_start]If an appointment is a no-show or fabricated, request a refund directly through the platform.
                    </p>
                </div>
              </div>

              <div className="mt-10">
                <Link to="/register" className="text-[#F39C12] font-bold flex items-center hover:underline">
                  View Marketplace <ChevronRight size={16} />
                </Link>
              </div>
            </div>

            {/* Illustration */}
            <div className="lg:w-1/2">
                <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                    alt="Data Analytics" 
                    className="rounded-2xl shadow-2xl border border-gray-700 opacity-90"
                />
            </div>

          </div>
        </div>
      </div>

      {/* --- BLOCK 4: TECHNOLOGY ADVANTAGE --- */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Built for Speed & Clarity</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <div className="flex items-start">
                    <Zap className="text-[#2E86DE] mt-1 mr-4" size={24} />
                    <div className="text-left">
                        <h4 className="font-bold text-lg">Dedicated Query Channels</h4>
                        <p className="text-gray-600 text-sm mt-1">We eliminate confusion by assigning a separate chatbox to every single lead.</p>
                    </div>
                </div>
                <div className="flex items-start">
                    <Layout className="text-[#2E86DE] mt-1 mr-4" size={24} />
                    <div className="text-left">
                        <h4 className="font-bold text-lg">Modern Design Philosophy</h4>
                        <p className="text-gray-600 text-sm mt-1">Clean, lucrative design ensuring simplicity and a smooth user experience.</p>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* --- FOOTER (Simplified Reuse) --- */}
      <div className="bg-[#10AC84] py-12 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">Ready to Join the Marketplace?</h2>
        <div className="flex justify-center gap-4">
            <Link to="/register" className="bg-white text-[#10AC84] px-6 py-3 rounded-lg font-bold shadow-lg hover:bg-gray-100 transition">Get Started</Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#222F3E] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
                <div className="col-span-1 md:col-span-2">
                    <h3 className="text-2xl font-bold mb-4 flex items-center">
                        RevenueSetters<span className="text-[#F39C12]">Pro</span>
                    </h3>
                    <p className="text-gray-400 leading-relaxed max-w-sm">
                        The premier marketplace for revenue-ready appointments. Connecting businesses with verified leads since 2023.
                    </p>
                    <div className="mt-6 flex space-x-4">
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Twitter size={18} /></a>
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Facebook size={18} /></a>
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Linkedin size={18} /></a>
                        <a href="#" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><Instagram size={18} /></a>
                    </div>
                </div>
                <div>
                    <h3 className="text-lg font-bold mb-6">Quick Links</h3>
                    <ul className="space-y-4">
                        <li><Link to="/" className="text-gray-400 hover:text-[#F39C12] transition">Home</Link></li>
                        <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#F39C12] transition">How It Works</Link></li>
                        <li><Link to="/register" className="text-gray-400 hover:text-[#F39C12] transition">For Companies</Link></li>
                        <li><Link to="/register" className="text-gray-400 hover:text-[#F39C12] transition">For Setters</Link></li>
                        <li><Link to="/marketplace" className="text-gray-400 hover:text-[#F39C12] transition">Marketplace</Link></li>
                    </ul>
                </div>
                <div>
                    <h3 className="text-lg font-bold mb-6">Legal</h3>
                    <ul className="space-y-4">
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Privacy Policy</a></li>
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Terms & Conditions</a></li>
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Refund Policy</a></li>
                        <li><a href="#" className="text-gray-400 hover:text-[#F39C12] transition">Contact Us</a></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
                <p className="text-gray-500 text-sm">
                    &copy; 2025 RevenueSetters. All rights reserved.
                </p>
                <p className="text-gray-600 text-sm mt-2 md:mt-0">
                    Designed for Growth.
                </p>
            </div>
        </div>
      </footer>

    </div>
  );
};

export default HowItWorks;